// The API `location` field currently carries an IANA time zone ("Africa/Accra").
// These helpers keep that technical value out of the UI unless it can be shown
// as something meaningful to a parent.

const TIME_ZONE_PATTERN = /^[A-Za-z_]+(?:\/[A-Za-z_+\-0-9]+){1,2}$/;

/** True when a value looks like an IANA time zone identifier. */
export const isTimeZoneValue = (value) =>
  typeof value === 'string' && TIME_ZONE_PATTERN.test(value.trim());

const readableCity = (timeZone) => {
  const segments = timeZone.trim().split('/');
  return segments[segments.length - 1].replace(/_/g, ' ');
};

/**
 * Turn "Africa/Accra" into "Accra (GMT+0)" — a label parents can read.
 * Returns null when the runtime rejects the time zone.
 */
export const formatTimeZoneLabel = (timeZone) => {
  if (!isTimeZoneValue(timeZone)) return null;

  const city = readableCity(timeZone);

  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: timeZone.trim(),
      timeZoneName: 'shortOffset',
    }).formatToParts(new Date());

    const offset = parts.find((part) => part.type === 'timeZoneName');
    return offset ? `${city} (${offset.value})` : city;
  } catch (error) {
    // Unknown / unsupported zone: fall back to the city name only.
    return city;
  }
};

/** Current local time at the school, e.g. "3:42 PM". Null when unavailable. */
export const formatLocalTime = (timeZone, now = new Date()) => {
  if (!isTimeZoneValue(timeZone)) return null;

  try {
    return new Intl.DateTimeFormat('en-US', {
      timeZone: timeZone.trim(),
      hour: 'numeric',
      minute: '2-digit',
    }).format(now);
  } catch (error) {
    return null;
  }
};
